"use client"

import { FC, useEffect, useRef, useState } from "react"
import type { SearchResult } from "./types"
import { MagnifierIcon, ClearIcon } from "./icons"
import {
  FieldWrapper,
  Field,
  FieldIcon,
  FieldButton,
  Input,
  Suggestions,
  SuggestionItem,
} from "./styles"

interface SearchFieldProps {
  value: string
  placeholder: string
  ariaLabel: string
  ariaClear: string
  ariaSuggestions: string
  suggestions: SearchResult[]
  onChange: (value: string) => void
  onSubmit: (value: string) => void
  onClear: () => void
  onSelectSuggestion: (result: SearchResult) => void
}

const SearchField: FC<SearchFieldProps> = ({
  value,
  placeholder,
  ariaLabel,
  ariaClear,
  ariaSuggestions,
  suggestions,
  onChange,
  onSubmit,
  onClear,
  onSelectSuggestion,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const blurTimeoutRef = useRef<number | undefined>(undefined)
  const [focused, setFocused] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => () => window.clearTimeout(blurTimeoutRef.current), [])

  const canSuggest =
    focused && value.trim().length > 1 && suggestions.length > 0
  const showSuggestions = canSuggest

  useEffect(() => {
    setActiveIndex(-1)
  }, [suggestions])

  const submit = (q: string) => {
    setFocused(false)
    setActiveIndex(-1)
    inputRef.current?.blur()
    onSubmit(q)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % suggestions.length)
      return
    }
    if (showSuggestions && e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
      return
    }
    if (e.key === "Enter") {
      if (showSuggestions && activeIndex >= 0) {
        e.preventDefault()
        onSelectSuggestion(suggestions[activeIndex])
        return
      }
      if (value.trim().length > 0) submit(value.trim())
      return
    }
    if (e.key === "Escape") {
      setFocused(false)
      setActiveIndex(-1)
    }
  }

  return (
    <FieldWrapper>
      <Field>
        <FieldIcon aria-hidden="true">
          <MagnifierIcon />
        </FieldIcon>
        <Input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls="search-suggestions"
          aria-autocomplete="list"
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // EGDE-400: the clear (X) button blurs the input and then refocuses
            // it synchronously — cancel the pending blur timeout or it flips
            // `focused` back to false and suggestions never reopen while typing.
            window.clearTimeout(blurTimeoutRef.current)
            setFocused(true)
          }}
          onBlur={() => {
            blurTimeoutRef.current = window.setTimeout(
              () => setFocused(false),
              120,
            )
          }}
        />
        {value.length > 0 && (
          <FieldButton
            type="button"
            aria-label={ariaClear}
            onClick={() => {
              onClear()
              inputRef.current?.focus()
            }}
          >
            <ClearIcon />
          </FieldButton>
        )}
      </Field>

      {showSuggestions && (
        <Suggestions
          id="search-suggestions"
          role="listbox"
          aria-label={ariaSuggestions}
        >
          {suggestions.map((s, i) => (
            <SuggestionItem
              key={s.url}
              role="option"
              aria-selected={i === activeIndex}
            >
              <button
                type="button"
                data-active={i === activeIndex}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelectSuggestion(s)}
              >
                {s.title}
              </button>
            </SuggestionItem>
          ))}
        </Suggestions>
      )}
    </FieldWrapper>
  )
}

export default SearchField
